import { RolesEnum } from '../constants/roles';

export interface RolePermissions {
    showMenu: boolean;
    canManageDocuments: boolean;
    canManageRoles: boolean;
    canAccessDataAnalytics: boolean;
    canAddDocuments: boolean;
    canDeleteDocuments: boolean;
}

export interface RoleDefinition {
    id: number;
    name: string;
    description: string;
    permissions: RolePermissions;
}

const STORAGE_KEY = 'role_definitions_v3';

const defaultRoles: RoleDefinition[] = [
    {
        id: RolesEnum.Member,
        name: 'Member',
        description: 'Default user role with restricted access.',
        permissions: {
            showMenu: false,
            canManageDocuments: false,
            canManageRoles: false,
            canAccessDataAnalytics: false,
            canAddDocuments: false,
            canDeleteDocuments: false,
        },
    },
    {
        id: RolesEnum.Admin,
        name: 'Admin',
        description: 'Administrative role with elevated access.',
        permissions: {
            showMenu: true,
            canManageDocuments: true,
            canManageRoles: true,
            canAccessDataAnalytics: true,
            canAddDocuments: true,
            canDeleteDocuments: true,
        },
    },
    {
        id: RolesEnum.Manager,
        name: 'Manager',
        description: 'Manager role with limited administrative capabilities.',
        permissions: {
            showMenu: false,
            canManageDocuments: true,
            canManageRoles: false,
            canAccessDataAnalytics: true,
            canAddDocuments: true,
            canDeleteDocuments: false,
        },
    },
    {
        id: RolesEnum.SuperAdmin,
        name: 'Super Admin',
        description: 'Super admin role with full access.',
        permissions: {
            showMenu: true,
            canManageDocuments: true,
            canManageRoles: true,
            canAccessDataAnalytics: true,
            canAddDocuments: true,
            canDeleteDocuments: true,
        },
    },
];

function hasWindow(): boolean {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function normalizeRole(role: any): RoleDefinition | null {
    if (!role || typeof role !== 'object') return null;
    const id = Number(role.id);
    if (Number.isNaN(id)) return null;

    const p = role.permissions || {};
    return {
        id,
        name: String(role.name || `Role ${id}`),
        description: String(role.description || ''),
        permissions: {
            showMenu: !!p.showMenu,
            canManageDocuments: !!p.canManageDocuments,
            canManageRoles: !!p.canManageRoles,
            canAccessDataAnalytics: !!p.canAccessDataAnalytics,
            canAddDocuments: !!p.canAddDocuments,
            canDeleteDocuments: !!p.canDeleteDocuments,
        },
    };
}

export function getRoleDefinitions(): RoleDefinition[] {
    if (!hasWindow()) return defaultRoles;
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return defaultRoles;
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return defaultRoles;
        const roles = parsed.map(normalizeRole).filter((r): r is RoleDefinition => !!r);
        return roles.length ? roles : defaultRoles;
    } catch {
        return defaultRoles;
    }
}

export function saveRoleDefinition(def: RoleDefinition): void {
    if (!hasWindow()) return;
    const roles = getRoleDefinitions();
    const idx = roles.findIndex((r) => r.id === def.id);
    if (idx >= 0) roles[idx] = def;
    else roles.push(def);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(roles));
}

export function deleteRoleDefinition(roleId: number): void {
    if (!hasWindow()) return;
    const roles = getRoleDefinitions();
    const next = roles.filter((r) => r.id !== roleId);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
}

export function getRoleById(roleId: number): RoleDefinition | undefined {
    return getRoleDefinitions().find((r) => r.id === roleId);
}

/** Member and Manager always use Deal Room–only routes and nav (ignores roleDefinitions showMenu overrides). */
export function isDealRoomOnlyRole(roleId: number): boolean {
    return roleId === RolesEnum.Member || roleId === RolesEnum.Manager;
}

export function canRoleShowMenu(roleId: number): boolean {
    const role = getRoleById(roleId);
    return !!role?.permissions?.showMenu;
}

export function canRoleViewSettings(roleId: number): boolean {
    const role = getRoleById(roleId);
    return !!role?.permissions?.canManageRoles;
}

export function canRoleManageDocuments(roleId: number): boolean {
    const role = getRoleById(roleId);
    return !!role?.permissions?.canManageDocuments;
}

export function canRoleDeleteDocuments(roleId: number): boolean {
    const role = getRoleById(roleId);
    return !!role?.permissions?.canDeleteDocuments;
}
